import React, { useEffect, useMemo, useState } from "react";
import {
  listPriceCodes, createPriceCode, updatePriceCode, deletePriceCode,
} from "../lib/api";
import { UNIT_LABEL, UNITS } from "../lib/calculator";
import RawNumInput from "../components/measure/RawNumInput";
import useCollapseMap from "../lib/useCollapseMap";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search, Tag, ChevronDown, ChevronRight, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import PriceListExcelDialog from "../components/pricecodes/PriceListExcelDialog";

const TRADES = [
  { value: "painting", label: "Schilderwerken" },
  { value: "flooring", label: "Vloerwerken" },
  { value: "plastering", label: "Pleisterwerken" },
  { value: "inspection", label: "Inspectie" },
  { value: "other", label: "Andere" },
];

const empty = {
  code: "", trade_category: "painting", work_category: "",
  description: "", unit: "m2", default_price: 0, vat_rate: 21,
  active: true, notes: "", apply_wall_deductions: false,
};

export default function PriceCodes() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [tradeFilter, setTradeFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [confirmDel, setConfirmDel] = useState(null);
  const [excelOpen, setExcelOpen] = useState(false);

  const refresh = () => listPriceCodes().then(setItems).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    if (!form.code.trim()) return toast.error("Code is verplicht");
    if (!form.description.trim()) return toast.error("Omschrijving is verplicht");
    try {
      const payload = {
        ...form,
        default_price: parseFloat(form.default_price) || 0,
        vat_rate: parseInt(form.vat_rate),
      };
      if (editing) {
        await updatePriceCode(editing.id, payload);
        toast.success("Prijscode bijgewerkt");
      } else {
        await createPriceCode(payload);
        toast.success("Prijscode aangemaakt");
      }
      setOpen(false);
      refresh();
    } catch { toast.error("Fout bij opslaan"); }
  };

  const remove = async (id) => {
    try {
      await deletePriceCode(id);
      toast.success("Verwijderd");
      setConfirmDel(null);
      refresh();
    } catch { toast.error("Fout bij verwijderen"); }
  };

  const filtered = items.filter((i) => {
    if (tradeFilter !== "all" && i.trade_category !== tradeFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      i.code.toLowerCase().includes(q) ||
      (i.description || "").toLowerCase().includes(q) ||
      (i.work_category || "").toLowerCase().includes(q)
    );
  });

  // Group by work_category (preserve insertion order). Codes within each group
  // are sorted by code asc so the user sees S-01.01, S-01.02, S-01.03...
  const grouped = (() => {
    const map = new Map();
    for (const it of filtered) {
      const key = it.work_category || "Overige";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    // Sort group keys: codes starting with S-NN order first, then alphabetical
    const sortedKeys = Array.from(map.keys()).sort((a, b) => {
      const ma = a.match(/^S-(\d+)/);
      const mb = b.match(/^S-(\d+)/);
      if (ma && mb) return parseInt(ma[1]) - parseInt(mb[1]);
      if (ma) return -1;
      if (mb) return 1;
      return a.localeCompare(b);
    });
    return sortedKeys.map((k) => [k, map.get(k).sort((a, b) => a.code.localeCompare(b.code))]);
  })();

  // v6 F3 — Persisted accordion state per work_category. Default = collapsed.
  // groupedIds is used by useCollapseMap to power expand/collapseAll.
  const groupedIds = useMemo(() => grouped.map(([cat]) => ({ id: cat })), [grouped]);
  const {
    isCollapsed: isGroupCollapsed,
    toggle: toggleCollapse,
    expandAll: expandAllGroups,
    collapseAll: collapseAllGroups,
  } = useCollapseMap("emma:listCollapsed:priceCodes", groupedIds, true);

  return (
    <div className="space-y-6" data-testid="prijslijst-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#5D7259]">
            <Tag className="inline w-3 h-3 mr-1" /> Prijscodes
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2C2C2A] mt-2 leading-tight">Prijslijst</h1>
          <p className="text-[#4F4F4D] mt-1">Beheer je codes, eenheden en prijzen <strong className="text-[#2C2C2A]">excl. BTW</strong>. Wordt automatisch gebruikt in nieuwe metingen.</p>
          <p className="text-xs text-[#5D7259] mt-1">Alle prijzen zijn excl. BTW. BTW wordt op offerte-/projectniveau toegepast.</p>
        </div>
        <div className="flex flex-wrap sm:flex-nowrap gap-3 w-full sm:w-auto">
          <Select value={tradeFilter} onValueChange={setTradeFilter}>
            <SelectTrigger className="h-14 w-full sm:w-48 rounded-xl bg-white" data-testid="trade-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle vakgebieden</SelectItem>
              {TRADES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4F4F4D]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Zoeken..."
              className="h-14 pl-10 w-full sm:w-56 bg-white rounded-xl" data-testid="pricecode-search" />
          </div>
          <Button
            variant="outline"
            onClick={() => setExcelOpen(true)}
            className="h-14 px-4 rounded-xl border-[#E5E2DC] hover:border-[#D96C4A] gap-2 flex-shrink-0"
            data-testid="excel-import-export-btn"
          >
            <FileSpreadsheet className="w-4 h-4" />
            <span className="whitespace-nowrap hidden sm:inline">Import / Export</span>
            <span className="whitespace-nowrap sm:hidden">Excel</span>
          </Button>
          <Button onClick={openNew} className="h-14 px-6 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] gap-2 flex-shrink-0" data-testid="new-pricecode-btn">
            <Plus className="w-4 h-4" /> <span className="whitespace-nowrap">Nieuwe code</span>
          </Button>
        </div>
      </div>

      <PriceListExcelDialog
        open={excelOpen}
        onOpenChange={setExcelOpen}
        onImported={refresh}
      />

      {/* v6 F3 — Bulk expand/collapse toolbar (only when >1 group) */}
      {grouped.length > 1 && (
        <div className="flex items-center justify-end gap-2 -mb-2" data-testid="pricecodes-bulk-toolbar">
          <button
            onClick={expandAllGroups}
            className="text-xs text-[#D96C4A] hover:underline px-2 py-1"
            data-testid="expand-all-pricecode-groups"
          >
            Alles open
          </button>
          <span className="text-xs text-[#A8A7A0]">·</span>
          <button
            onClick={collapseAllGroups}
            className="text-xs text-[#4F4F4D] hover:underline px-2 py-1"
            data-testid="collapse-all-pricecode-groups"
          >
            Alles dicht
          </button>
        </div>
      )}

      <div className="emma-card !p-0 overflow-hidden hidden md:block" data-testid="pricecodes-table">        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F0EFEA] text-left text-xs uppercase tracking-wide text-[#4F4F4D]">
              <tr>
                <th className="px-4 py-3 font-bold">Code</th>
                <th className="px-4 py-3 font-bold">Omschrijving</th>
                <th className="px-4 py-3 font-bold">Eenheid</th>
                <th className="px-4 py-3 font-bold text-right">Prijs (excl.)</th>
                <th className="px-4 py-3 font-bold">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E2DC]">
              {grouped.length === 0 && (
                <tr><td colSpan={6} className="py-8">
                  <div className="text-center space-y-1.5">
                    <p className="text-sm text-[#4F4F4D]">Nog geen prijscodes.</p>
                    <p className="text-xs text-[#A8A7A0]">Voeg een code toe of importeer je prijslijst.</p>
                  </div>
                </td></tr>
              )}
              {grouped.map(([category, rows]) => {
                const isCollapsed = isGroupCollapsed(category);
                return (
                <React.Fragment key={category}>
                  <tr className="bg-[#FAF9F6] cursor-pointer hover:bg-[#F0EFEA]" data-testid={`pricecode-group-${category}`}
                      onClick={() => toggleCollapse(category)}>
                    <td colSpan={6} className="px-4 py-2.5 font-heading font-bold text-[#2C2C2A] text-sm tracking-wide">
                      <span className="inline-flex items-center gap-2">
                        {isCollapsed ? <ChevronRight className="w-4 h-4 text-[#5D7259]" /> : <ChevronDown className="w-4 h-4 text-[#5D7259]" />}
                        {category} <span className="text-[#5D7259] font-normal text-xs ml-1">({rows.length})</span>
                      </span>
                    </td>
                  </tr>
                  {!isCollapsed && rows.map((it) => (
                    <tr key={it.id} className="hover:bg-[#FAF9F6]" data-testid={`pricecode-row-${it.id}`}>
                      <td className="px-4 py-3 font-mono font-semibold text-[#2C2C2A]">{it.code}</td>
                      <td className="px-4 py-3">{it.description}</td>
                      <td className="px-4 py-3">{UNIT_LABEL[it.unit]}</td>
                      <td className="px-4 py-3 text-right font-semibold">€ {(+it.default_price).toFixed(2)}</td>
                      <td className="px-4 py-3">
                        {it.active ? (
                          <Badge className="bg-[#6B8E6B] text-white">Actief</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[#4F4F4D]">Inactief</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => openEdit(it)} className="w-9 h-9 rounded-lg hover:bg-[#F0EFEA] flex items-center justify-center" data-testid={`edit-pricecode-${it.id}`}>
                            <Pencil className="w-4 h-4 text-[#4F4F4D]" />
                          </button>
                          <button onClick={() => setConfirmDel(it)} className="w-9 h-9 rounded-lg hover:bg-red-50 flex items-center justify-center" data-testid={`delete-pricecode-${it.id}`}>
                            <Trash2 className="w-4 h-4 text-[#D9534F]" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile card view — grouped per category */}
      <div className="md:hidden space-y-4" data-testid="pricecodes-mobile">
        {grouped.length === 0 && (
          <div className="emma-card text-center py-8 space-y-1.5">
            <p className="text-sm text-[#4F4F4D]">Nog geen prijscodes.</p>
            <p className="text-xs text-[#A8A7A0]">Voeg een code toe of importeer je prijslijst.</p>
          </div>
        )}
        {grouped.map(([category, rows]) => {
          const isCollapsed = isGroupCollapsed(category);
          return (
          <div key={category} className="space-y-2" data-testid={`pricecode-group-mobile-${category}`}>
            <button onClick={() => toggleCollapse(category)}
              className="w-full font-heading font-bold text-[#2C2C2A] px-1 pt-2 flex items-baseline gap-2 hover:text-[#D96C4A] transition text-left"
              data-testid={`pricecode-group-mobile-toggle-${category}`}>
              {isCollapsed ? <ChevronRight className="w-4 h-4 self-center" /> : <ChevronDown className="w-4 h-4 self-center" />}
              <span>{category}</span>
              <span className="text-[#5D7259] font-normal text-xs">({rows.length})</span>
            </button>
            {!isCollapsed && rows.map((it) => (
              <div key={it.id} className="emma-card" data-testid={`pricecode-card-${it.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono font-bold text-[#2C2C2A]">{it.code}</div>
                    <div className="text-sm text-[#2C2C2A] mt-0.5">{it.description}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {it.active ? (
                      <Badge className="bg-[#6B8E6B] text-white">Actief</Badge>
                    ) : (
                      <Badge variant="outline">Inactief</Badge>
                    )}
                    <span className="font-bold text-lg">€ {(+it.default_price).toFixed(2)}</span>
                    <span className="text-xs text-[#4F4F4D]">{UNIT_LABEL[it.unit]}</span>
                  </div>
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t border-[#E5E2DC]">
                  <Button onClick={() => openEdit(it)} variant="outline" className="flex-1 h-11 rounded-xl gap-1 text-xs">
                    <Pencil className="w-3.5 h-3.5" /> Bewerk
                  </Button>
                  <Button onClick={() => setConfirmDel(it)} variant="outline" className="h-11 w-11 rounded-xl text-[#D9534F] border-[#D9534F]">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl w-[calc(100vw-2rem)] sm:w-full p-0 max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] flex flex-col gap-0 overflow-hidden"
          data-testid="pricecode-dialog"
        >
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#E5E2DC]">
            <DialogTitle className="font-heading">{editing ? "Prijscode bewerken" : "Nieuwe prijscode"}</DialogTitle>
            <DialogDescription>Definieer een herbruikbare lijn voor je offertes.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Code *</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="h-12 mt-1.5 rounded-xl font-mono" placeholder="bv. SCH-MUUR-001" data-testid="pricecode-code-input" />
            </div>
            <div>
              <Label>Vakgebied</Label>
              <Select value={form.trade_category} onValueChange={(v) => setForm({ ...form, trade_category: v })}>
                <SelectTrigger className="h-12 mt-1.5 rounded-xl" data-testid="pricecode-trade-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TRADES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Omschrijving *</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="h-12 mt-1.5 rounded-xl" data-testid="pricecode-description-input" />
            </div>
            <div>
              <Label>Werkcategorie</Label>
              <Input value={form.work_category} onChange={(e) => setForm({ ...form, work_category: e.target.value })}
                className="h-12 mt-1.5 rounded-xl" placeholder="bv. Voorbereiding" data-testid="pricecode-work-input" />
            </div>
            <div>
              <Label>Eenheid</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger className="h-12 mt-1.5 rounded-xl" data-testid="pricecode-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {UNITS.map((u) => <SelectItem key={u} value={u}>{UNIT_LABEL[u]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Standaard prijs (€ excl. BTW)</Label>
              <RawNumInput value={form.default_price}
                allowEmpty
                onChange={(v) => setForm({ ...form, default_price: v })}
                className="h-12 mt-1.5 rounded-xl font-mono" data-testid="pricecode-price-input" />
              <p className="text-xs text-[#4F4F4D] mt-1.5">BTW wordt op project-niveau ingesteld, niet hier.</p>
            </div>
            <div className="sm:col-span-2 flex items-center justify-between border border-[#E5E2DC] rounded-xl px-4 h-12">
              <Label className="cursor-pointer">Actief (zichtbaar in metingen)</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="pricecode-active-switch" />
            </div>
            {/* v6 E3 — wand-aftrekken alleen relevant voor m² codes */}
            {form.unit === "m2" && (
              <div className="sm:col-span-2 flex items-center justify-between border border-[#E5E2DC] rounded-xl px-4 h-auto py-2">
                <div className="flex-1 pr-3">
                  <Label className="cursor-pointer block">Netto wandoppervlakte (aftrekposten toepassen)</Label>
                  <p className="text-[11px] text-[#5D7259] mt-0.5">
                    AAN voor alle wand-gebonden werken (muren schilderen, behang, plamuren, schuren, reinigen, primer muren …).
                    Aftrekposten (ramen, deuren, inbouwkasten) worden afgetrokken van de bruto wandoppervlakte.
                    UIT voor plafonds, vloeren of werken op vaste bruto-oppervlakte.
                  </p>
                  <p className="text-[11px] text-[#6E6E6C] mt-1">
                    <strong>Voorbeeld:</strong> bruto 60 m² − 12 m² aftrekken = <strong>48 m² netto</strong> waarop deze code rekent.
                  </p>
                </div>
                <Switch
                  checked={!!form.apply_wall_deductions}
                  onCheckedChange={(v) => setForm({ ...form, apply_wall_deductions: v })}
                  data-testid="pricecode-apply-wall-deductions-switch"
                />
              </div>
            )}
            <div className="sm:col-span-2">
              <Label>Notities</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2} className="mt-1.5 rounded-xl" data-testid="pricecode-notes-input" />
            </div>
          </div>
          <DialogFooter
            className="px-5 py-3 border-t border-[#E5E2DC] bg-white flex-row gap-2 sm:flex-row"
            style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
          >
            <Button variant="outline" className="h-12 rounded-xl flex-1 sm:flex-none" onClick={() => setOpen(false)}>Annuleer</Button>
            <Button onClick={save} className="h-12 rounded-xl bg-[#D96C4A] hover:bg-[#C05A3C] flex-1 sm:flex-none" data-testid="pricecode-save-btn">
              {editing ? "Bijwerken" : "Opslaan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Prijscode verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>"{confirmDel?.code}" wordt verwijderd. Bestaande offertes blijven intact.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleer</AlertDialogCancel>
            <AlertDialogAction className="bg-[#D9534F] hover:bg-[#B23A3A]" onClick={() => remove(confirmDel.id)}>
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
